import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Trash2, ShoppingBag, Star } from "lucide-react";
import { toast } from "sonner";

export default function Cart() {
  const nav = useNavigate();
  const [cart, setCart] = useState({ lines: [], total: 0 });
  const [loyalty, setLoyalty] = useState(0);
  const [open, setOpen] = useState(false);
  const [checkout, setCheckout] = useState({ delivery_address: "", notes: "", use_loyalty_points: 0 });

  const load = async () => {
    const [c, l] = await Promise.all([api.get("/dealer/cart"), api.get("/dealer/loyalty")]);
    setCart(c.data);
    setLoyalty(l.data.account?.balance || 0);
  };
  useEffect(() => { load(); }, []);

  const updateQty = async (line, qty) => {
    if (qty < 0) return;
    try { const { data } = await api.post("/dealer/cart", { product_id: line.product_id, qty }); setCart(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (line) => { const { data } = await api.delete(`/dealer/cart/${line.product_id}`); setCart(data); };

  const placeOrder = async () => {
    try {
      const { data: order } = await api.post("/dealer/orders", checkout);
      setOpen(false);
      toast.success(`Order ${order.number} created — redirecting to payment…`);
      // immediately initiate Stripe checkout
      const { data: sess } = await api.post("/dealer/checkout/session", {
        order_id: order.id, origin_url: window.location.origin
      });
      if (sess.url) window.location.href = sess.url;
      else nav(`/dealer/orders/${order.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const maxRedeem = Math.min(loyalty, Math.floor(cart.total * 0.20));
  const finalAmount = (cart.total - (checkout.use_loyalty_points || 0)).toFixed(2);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="dealer-cart">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-black">Cart</h1>
        <p className="text-muted-foreground">Review items, redeem loyalty points and check out via Stripe.</p>
      </div>

      {cart.lines.length === 0 ? (
        <Card><CardContent className="p-12 text-center"><ShoppingBag className="w-10 h-10 mx-auto text-muted-foreground mb-3" /><div className="text-muted-foreground">Your cart is empty.</div><Button className="mt-4" onClick={() => nav("/dealer/products")}>Browse catalogue</Button></CardContent></Card>
      ) : (
        <>
          <div className="surface-card p-4 space-y-3">
            {cart.lines.map((l) => (
              <div key={l.product_id} className="flex items-center gap-4 border-b pb-3 last:border-b-0" data-testid={`cart-line-${l.product_id}`}>
                {l.image && <img src={l.image} alt={l.title} className="w-16 h-16 object-cover rounded" />}
                <div className="flex-1 min-w-0"><div className="font-semibold">{l.title}</div><div className="text-xs text-muted-foreground">₹{l.unit_price.toLocaleString()}/unit</div></div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => updateQty(l, l.qty - 1)} data-testid={`cart-minus-${l.product_id}`}>−</Button>
                  <span className="font-mono w-8 text-center">{l.qty}</span>
                  <Button size="sm" variant="outline" onClick={() => updateQty(l, l.qty + 1)} data-testid={`cart-plus-${l.product_id}`}>+</Button>
                </div>
                <div className="w-24 text-right font-semibold">₹{l.subtotal.toLocaleString()}</div>
                <Button size="sm" variant="ghost" onClick={() => remove(l)} data-testid={`cart-rm-${l.product_id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
            <div className="flex items-center justify-between pt-2">
              <div className="text-sm flex items-center gap-2"><Star className="w-4 h-4 text-violet-600" /> {loyalty} loyalty points available · max ₹{maxRedeem} redeemable (20% cap)</div>
              <div className="font-display text-2xl font-black">Total: ₹{cart.total.toLocaleString()}</div>
            </div>
          </div>
          <div className="flex justify-end"><Button size="lg" onClick={() => setOpen(true)} data-testid="cart-checkout"><ShoppingBag className="w-4 h-4 mr-1" /> Checkout</Button></div>
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Place order</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Delivery address</Label><Input value={checkout.delivery_address} onChange={(e) => setCheckout({ ...checkout, delivery_address: e.target.value })} data-testid="checkout-address" placeholder="GST address, contact person, phone…" /></div>
            <div><Label>Notes (optional)</Label><Input value={checkout.notes} onChange={(e) => setCheckout({ ...checkout, notes: e.target.value })} /></div>
            <div>
              <Label>Redeem loyalty points (max {maxRedeem})</Label>
              <Input type="number" min="0" max={maxRedeem} value={checkout.use_loyalty_points} onChange={(e) => setCheckout({ ...checkout, use_loyalty_points: Math.min(parseInt(e.target.value || "0", 10), maxRedeem) })} data-testid="checkout-redeem" />
            </div>
            <div className="bg-muted/30 p-3 rounded text-sm">
              <div className="flex justify-between"><span>Subtotal</span><span>₹{cart.total.toLocaleString()}</span></div>
              <div className="flex justify-between text-violet-700"><span>Loyalty redemption</span><span>− ₹{(checkout.use_loyalty_points || 0).toLocaleString()}</span></div>
              <div className="flex justify-between font-display text-lg font-black border-t pt-1 mt-1"><span>Pay</span><span>₹{Number(finalAmount).toLocaleString()}</span></div>
              <div className="text-[10px] text-muted-foreground mt-1">You will earn ~{Math.floor(Number(finalAmount) * 0.02)} loyalty points (2% cashback) after payment.</div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={placeOrder} disabled={!checkout.delivery_address} data-testid="checkout-pay">Pay with Stripe</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
